import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Search,
  Pencil,
  Wifi,
  Clock,
  Shield,
  LayoutDashboard,
  BarChart3,
  Cloud,
  Wrench,
  Terminal,
  Tag,
  Calendar,
} from "lucide-react";
import { Button } from "@mui/material";
import RefreshButton from "../../lib/components/RefreshButton";
import { TEXT_LIBRARY, DEFAULT_TIME_RANGE } from "../../lib/utils/constants";
import { RESET_ZOOM_BUTTON_SX } from "../charger_metrics/constants";
import ErrorCard from "../../lib/components/ErrorCard";
import { useGlobalFilters } from "../../lib/context/GlobalFilterProvider";
import useChargerConfig from "./hooks/useChargerConfig";
import { formatDate, getTimezoneOffset, getRange, getRelativeTime } from "../../lib/utils/utility";
import DateRangeToolbar from "../fleet_dashboard/DateRangeToolbar";
import { chargerDetailsMap, fieldGroups, GROUP_ICONS } from "./constants";
import { ChargerMetrics } from "../charger_metrics";
import OcppMessages from "../ocpp_messages";
import Events from "../events";
import SlideOutPanel from "../ocpp_messages/SlideOutPanel";
import { Troubleshoot } from "../logs_download";
import SendCommands from "./SendCommands";
import { fetchFleetSearchDetails } from "../fleet_metrics/hooks/useFleetSearchHook/api";
import chargerImg1 from "../../assets/charger_gen1.jpg";
import chargerImg2 from "../../assets/charger_gen2.jpg";
import "./styles.scss";
import { useParams, useSearchParams } from "react-router-dom";
import { checkFeatureAccess, FEATURES, getCompanyDisplayName, isEnphaseUser } from "../../lib/utils/featureAccess";
import useHierarchy from "../../lib/hooks/useHierarchy";

const VALID_TABS = ["overview", "metrics", "ocpp", "troubleshoot", "events", "commands"];

const BASE_TAB_CONFIG = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "metrics", label: "Metrics", icon: BarChart3 },
  { id: "ocpp", label: "Cloud Logs", icon: Cloud },
  { id: "troubleshoot", label: "Device Logs", icon: Wrench },
  { id: "events", label: "Events", icon: Calendar },
  { id: "commands", label: "Commands", icon: Terminal, featureId: FEATURES.COMMANDS },
];

const CHARGER_IMAGES = [chargerImg1, chargerImg2];

export function EvSummary() {
  const { setState, serialNumber: globalSerialNumber, siteId: globalSiteId } = useGlobalFilters();
  const { chargerId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { config, loading, error, sendCommand } = useChargerConfig(globalSiteId, globalSerialNumber);

  const enphaseUserFlag = isEnphaseUser();
  const tabConfig = BASE_TAB_CONFIG;

  const [isSerialEditorOpen, setIsSerialEditorOpen] = useState(false);
  const [serialQuery, setSerialQuery] = useState("");
  const [debouncedSerialQuery, setDebouncedSerialQuery] = useState("");
  const [fleetOptions, setFleetOptions] = useState([]);
  const [isSerialLoading, setIsSerialLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [commandHistory] = useState([]);
  const [stationName, setStationName] = useState(null);
  const [openChargerDetailsSlideout, setOpenChargerDetailsSlideout] = useState(false);
  const serialEditorRef = useRef(null);

  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(VALID_TABS.includes(tabFromUrl) ? tabFromUrl : "overview");

  function setActiveTabWithParam(tab) {
    setActiveTab(tab);
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        return next;
      },
      { replace: true }
    );
  }
  const [dateRange, setDateRange] = useState(() => getRange(DEFAULT_TIME_RANGE));

  // DateRangeToolbar state
  const [activePreset, setActivePreset] = useState(DEFAULT_TIME_RANGE);

  const handlePresetChange = useCallback(value => {
    setActivePreset(value);
    const newRange = getRange(value);
    setDateRange(newRange);
    setState({ dateRange: newRange });
  }, []);

  const handleCustomChange = useCallback((range, type) => {
    setDateRange(range);
    setActivePreset(type);
    setState({ dateRange: range });
  }, []);

  const companyName = getCompanyDisplayName();

  useEffect(() => {
    if (chargerId && chargerId !== globalSerialNumber) {
      setState({ serialNumber: chargerId });
    }
  }, [chargerId, globalSerialNumber, setState]);

  // Deterministic charger image based on serial number
  const chargerImage = useMemo(() => {
    const seed = globalSerialNumber || "";
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = (hash << 5) - hash + seed.charCodeAt(i);
      hash |= 0;
    }
    return CHARGER_IMAGES[Math.abs(hash) % CHARGER_IMAGES.length];
  }, [globalSerialNumber]);

  function renderDashboardSkeleton() {
    return (
      <div className="evs-skeleton">
        <div className="skel skel-hero" />
        <div className="skel skel-tabs" />
        <div className="skel-stats-row">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skel skel-stat" style={{ animationDelay: `${i * 0.1}s` }} />
          ))}
        </div>
        <div className="skel-groups-row">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skel skel-card" style={{ animationDelay: `${i * 0.1}s` }} />
          ))}
        </div>
      </div>
    );
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSerialQuery(serialQuery);
    }, 250);

    return () => clearTimeout(handler);
  }, [serialQuery]);

  useEffect(() => {
    if (!isSerialEditorOpen && (globalSerialNumber || config?.serialNumber)) {
      setSerialQuery(globalSerialNumber || config?.serialNumber || "");
    }
  }, [globalSerialNumber, config?.serialNumber, isSerialEditorOpen]);

  // Resolve station name from cached hierarchy data
  const { stations: hierarchyStations } = useHierarchy({ enabled: !enphaseUserFlag });

  useEffect(() => {
    const siteId = config?.siteId || globalSiteId;
    if (siteId && hierarchyStations.length > 0) {
      let foundStationName = null;
      hierarchyStations.forEach(station => {
        if (station.sites && Array.isArray(station.sites)) {
          station.sites.forEach(site => {
            if (site.siteId === siteId) {
              foundStationName = station.stationName || station.name || "Unknown Station";
            }
          });
        }
      });
      setStationName(foundStationName);
    } else if (!siteId) {
      setStationName(null);
    }
  }, [config?.siteId, globalSiteId, hierarchyStations]);

  const dateKeys = new Set(["lastReportedAt", "createdAt", "warrantyDueDate", "warrantyStartDate", "updatedAt"]);

  function formatValue(key, value) {
    if (key === "timezone") {
      return `${value} (${getTimezoneOffset(value)})`;
    }
    if (key === "phaseMode") {
      const numValue = Number(value);
      if (numValue === 1 || numValue === 3) return "IT";
      if (numValue === 2 || numValue === 4) return "TN/TT";
      return value || "-";
    }
    if (key === "hoControl") {
      return !value ? "Yes" : "No";
    }
    if (dateKeys.has(key)) return formatDate(value);
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return value !== null && value !== undefined ? String(value) : "-";
  }

  async function onSerialEditorOpen() {
    setIsSerialEditorOpen(true);

    if (fleetOptions.length > 0) return;

    setIsSerialLoading(true);
    try {
      const response = await fetchFleetSearchDetails();
      const data = Array.isArray(response?.data) ? response.data : response?.data?.data || [];
      setFleetOptions(Array.isArray(data) ? data : []);
    } catch {
      setFleetOptions([]);
    } finally {
      setIsSerialLoading(false);
    }
  }

  function onSerialSelect(option) {
    if (!option?.serialNumber) return;

    setIsSerialEditorOpen(false);
    setSerialQuery(option.serialNumber);
    setState({ serialNumber: option.serialNumber, siteId: option.siteId });
  }

  function handleSearchKeyPress(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      const query = serialQuery.trim();
      if (query) {
        // Find exact match or first match
        const match = fleetOptions.find(
          option =>
            option.serialNumber?.toLowerCase() === query.toLowerCase() ||
            option.serialNumber?.toLowerCase().includes(query.toLowerCase())
        );

        if (match) {
          onSerialSelect(match);
        }
      }
    }
  }

  const serialSuggestions = useMemo(() => {
    const query = debouncedSerialQuery.trim().toLowerCase();
    if (!query) return [];
    const filtered = fleetOptions.filter(option => {
      const serial = option?.serialNumber ? String(option.serialNumber).toLowerCase() : "";
      if (!serial) return false;
      return query ? serial.includes(query) : true;
    });
    return filtered.slice(0, 5);
  }, [debouncedSerialQuery, fleetOptions]);

  useEffect(() => {
    if (!isSerialEditorOpen) return;

    const handleOutsideClick = event => {
      if (serialEditorRef.current && !serialEditorRef.current.contains(event.target)) {
        setIsSerialEditorOpen(false);
      }
    };

    window.addEventListener("mousedown", handleOutsideClick);
    return () => window.removeEventListener("mousedown", handleOutsideClick);
  }, [isSerialEditorOpen]);

  function handleRefreshClick() {
    const newRange = getRange(activePreset || DEFAULT_TIME_RANGE);
    setDateRange(newRange);
    setState({ dateRange: newRange });
  }

  const getStatusColor = status => {
    if (!status) return "";
    return status === "NORMAL" ? "green" : "red";
  };

  const getStatusLabel = status => {
    if (!status) return "-";
    if (status === "NORMAL") return "Online";
    return status;
  };

  const shouldShowSuggestions = isSerialEditorOpen && (serialQuery.trim().length > 0 || isSerialLoading);
  const hasSelectedChargerContext = Boolean(chargerId || globalSerialNumber);
  const isHydratingSelectedCharger = hasSelectedChargerContext && (!globalSiteId || !config);
  const shouldShowStandaloneSearchBar = !hasSelectedChargerContext && !globalSiteId && !config && !loading;

  function getValueClassName(key, value) {
    const formatted = formatValue(key, value);
    if (formatted === "Yes") return "evs-value--yes";
    if (formatted === "No") return "evs-value--no";
    if (key === "isConnected" && value === true) return "evs-value--connected";
    if (key === "isConnected" && value === false) return "evs-value--disconnected";
    if (key === "status" && value === "NORMAL") return "evs-value--normal";
    return "";
  }

  function renderOverviewTab(data) {
    if (!data) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* KPI Cards Row — matches Dashboard kpi-card style */}
        <div className="kpi-grid kpi-grid--charger">
          <motion.div
            className="kpi-card"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <div className="kpi-card__icon kpi-card__icon--green">
              <Tag size={22} />
            </div>
            <div className="kpi-card__content">
              <span className="kpi-card__label">SKU</span>
              <div className="kpi-card__value-row">
                <span className="kpi-card__value kpi-card__value--sm">{data.sku || "-"}</span>
              </div>
            </div>
          </motion.div>
          <motion.div
            className="kpi-card"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="kpi-card__icon kpi-card__icon--orange">
              <Wifi size={22} />
            </div>
            <div className="kpi-card__content">
              <span className="kpi-card__label">Connection</span>
              <div className="kpi-card__value-row">
                <span className="kpi-card__value kpi-card__value--sm">{data.activeConnection || "-"}</span>
              </div>
            </div>
          </motion.div>
          <motion.div
            className="kpi-card"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <div className="kpi-card__icon kpi-card__icon--blue">
              <Clock size={22} />
            </div>
            <div className="kpi-card__content">
              <span className="kpi-card__label">Last Reported</span>
              <div className="kpi-card__value-row">
                <span
                  className="kpi-card__value kpi-card__value--sm"
                  title={data.lastReportedAt ? formatDate(data.lastReportedAt) : ""}
                >
                  {data.lastReportedAt ? getRelativeTime(data.lastReportedAt) : "-"}
                </span>
              </div>
            </div>
          </motion.div>
          <motion.div
            className="kpi-card"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="kpi-card__icon kpi-card__icon--purple">
              <Shield size={22} />
            </div>
            <div className="kpi-card__content">
              <span className="kpi-card__label">Phase Config</span>
              <div className="kpi-card__value-row">
                <span className="kpi-card__value kpi-card__value--sm">
                  {data.phaseCount ? `${data.phaseCount}-Phase` : "-"}
                  {data.ratedCurrent ? ` / ${data.ratedCurrent}A` : ""}
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Data as of timestamp */}
        {data.lastReportedAt && (
          <motion.div
            className="evs-data-as-of"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Data as of {formatDate(data.lastReportedAt)}
          </motion.div>
        )}

        {/* Detail Group Cards — two fixed columns */}
        <div className="evs-group-grid">
          {["left", "right"].map(side => {
            const columnGroups =
              side === "left"
                ? fieldGroups.filter(g => g.title === "Device Information" || g.title === "Partner Integration")
                : fieldGroups.filter(g => g.title !== "Device Information" && g.title !== "Partner Integration");
            return (
              <div key={side} className="evs-group-col">
                {columnGroups.map((group, gi) => {
                  const groupIcon = GROUP_ICONS[group.title];
                  return (
                    <motion.div
                      key={group.title}
                      className="evs-group-card"
                      initial={{ opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.1 + gi * 0.06, duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <div className="evs-group-header">
                        {groupIcon && (
                          <div className={`evs-group-icon evs-group-icon--${groupIcon.color}`}>{groupIcon.emoji}</div>
                        )}
                        <div className="evs-group-title">{group.title}</div>
                      </div>
                      <div className="evs-group-rows">
                        {group.keys.map(key => {
                          if (!chargerDetailsMap[key]) return null;
                          return (
                            <div key={key} className="evs-group-row">
                              <div className="evs-row-label">{chargerDetailsMap[key]}</div>
                              <div className={`evs-row-value ${getValueClassName(key, data?.[key])}`}>
                                {formatValue(key, data?.[key])}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </motion.div>
    );
  }

  return (
    <div className="ev-summary-wrapper">
      {/* Compact Hero Banner — matches Dashboard & Fleet Search */}
      <div className="evs-hero evs-hero--compact">
        <svg className="evs-hero__particles" aria-hidden="true">
          {Array.from({ length: 15 }).map((_, i) => (
            <circle
              key={i}
              className="evs-hero__particle"
              cx={`${6 + ((i * 31) % 90)}%`}
              cy={`${8 + ((i * 17) % 85)}%`}
              r={1 + (i % 3)}
              style={{ animationDelay: `${(i * 0.4) % 6}s`, animationDuration: `${4 + (i % 4)}s` }}
            />
          ))}
        </svg>
        <div className="evs-hero__content">
          <div className="evs-hero__text">
            <span className="evs-hero__company">{companyName}</span>
            <h1 className="evs-hero__title">Charger Summary</h1>
            <p className="evs-hero__subtitle">Detailed charger configuration & monitoring</p>
          </div>
          {globalSiteId && !loading && !error && config && (
            <>
              <div className="evs-hero__image-wrapper">
                <div className="evs-hero__glow-ring" />
                <img className="evs-hero__charger-img" src={chargerImage} alt="EV Charger" />
              </div>
              <div className="evs-hero__live-stats">
                <div className="evs-hero__detail" ref={serialEditorRef}>
                  <span className="evs-hero__detail-label">Serial</span>
                  <span className="evs-hero__detail-value">
                    {globalSerialNumber || config?.serialNumber || "-"}
                    <button className="evs-hero__inline-btn" onClick={onSerialEditorOpen} title="Change serial number">
                      <Pencil size={11} />
                    </button>
                  </span>
                  {isSerialEditorOpen && (
                    <div className="evs-hero__search-popover">
                      <div className="evs-hero__search-input-wrapper">
                        <Search size={14} />
                        <input
                          type="text"
                          value={serialQuery}
                          onChange={e => setSerialQuery(e.target.value)}
                          onKeyPress={handleSearchKeyPress}
                          placeholder="Search for device..."
                          autoFocus // eslint-disable-line jsx-a11y/no-autofocus
                        />
                        {isSerialLoading && <div className="evs-hero__search-spinner" />}
                      </div>
                      {shouldShowSuggestions && (
                        <div className="evs-hero__search-results">
                          {isSerialLoading ? (
                            <div className="evs-hero__search-loading">Loading devices...</div>
                          ) : serialSuggestions.length > 0 ? (
                            serialSuggestions.map((option, index) => (
                              <div
                                key={`${option.serialNumber}-${index}`}
                                className="evs-hero__search-item"
                                role="button"
                                tabIndex={0}
                                onClick={() => onSerialSelect(option)}
                                onKeyDown={e => {
                                  if (e.key === "Enter" || e.key === " ") onSerialSelect(option);
                                }}
                              >
                                <div className="evs-hero__search-item-serial">{option.serialNumber}</div>
                                <div className="evs-hero__search-item-site">Site: {option.siteId}</div>
                              </div>
                            ))
                          ) : (
                            <div className="evs-hero__search-loading">No devices found</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="evs-hero__stat-divider" />
                <div className="evs-hero__detail">
                  <span className="evs-hero__detail-label">Site</span>
                  <span className="evs-hero__detail-value">{config?.siteId || globalSiteId || "-"}</span>
                </div>
                {stationName && !enphaseUserFlag && (
                  <>
                    <div className="evs-hero__stat-divider" />
                    <div className="evs-hero__detail">
                      <span className="evs-hero__detail-label">Station</span>
                      <span className="evs-hero__detail-value">{stationName}</span>
                    </div>
                  </>
                )}
                <div className="evs-hero__stat-divider" />
                <div className="evs-hero__detail">
                  <span className="evs-hero__detail-label">Status</span>
                  <span className="evs-hero__detail-value">
                    <span className={`evs-hero__badge evs-hero__badge--${getStatusColor(config?.status) || "default"}`}>
                      {config?.status === "NORMAL" && <span className="evs-hero__badge-dot" />}
                      {getStatusLabel(config?.status)}
                    </span>
                  </span>
                </div>
                {config?.firmwareVersion && (
                  <>
                    <div className="evs-hero__stat-divider" />
                    <div className="evs-hero__detail">
                      <span className="evs-hero__detail-label">Firmware</span>
                      <span className="evs-hero__detail-value">{config.firmwareVersion}</span>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Search Bar — outside hero so dropdown is not clipped */}
      {shouldShowStandaloneSearchBar && (
        <div className="evs-search-bar" ref={serialEditorRef}>
          <div className="evs-search-bar__input-wrap">
            <Search size={16} />
            <input
              type="text"
              value={serialQuery}
              onChange={e => {
                setSerialQuery(e.target.value);
                if (!isSerialEditorOpen) onSerialEditorOpen();
              }}
              onKeyPress={handleSearchKeyPress}
              onFocus={onSerialEditorOpen}
              placeholder="Search charger by serial number..."
            />
            {isSerialLoading && <div className="evs-hero__search-spinner" />}
          </div>
          {shouldShowSuggestions && (
            <div className="evs-search-bar__results">
              {isSerialLoading ? (
                <div className="evs-search-bar__empty">Loading devices...</div>
              ) : serialSuggestions.length > 0 ? (
                serialSuggestions.map((option, index) => (
                  <div
                    key={`${option.serialNumber}-${index}`}
                    className="evs-search-bar__item"
                    role="button"
                    tabIndex={0}
                    onClick={() => onSerialSelect(option)}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") onSerialSelect(option);
                    }}
                  >
                    <div className="evs-search-bar__item-serial">{option.serialNumber}</div>
                    <div className="evs-search-bar__item-site">Site: {option.siteId}</div>
                  </div>
                ))
              ) : (
                <div className="evs-search-bar__empty">No devices found</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Pill Tabs */}
      {globalSiteId && !loading && !error && config && (
        <motion.div
          className="evs-tabs"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="evs-tabs__inner">
            {tabConfig.map(tab => {
              if (tab.featureId && !checkFeatureAccess(tab.featureId)) return null;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`evs-tabs__btn ${activeTab === tab.id ? "evs-tabs__btn--active" : ""}`}
                  onClick={() => setActiveTabWithParam(tab.id)}
                >
                  <Icon size={14} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Content Area */}
      <div className="content-area">
        {!error && (loading || isHydratingSelectedCharger) && renderDashboardSkeleton()}
        {error && <ErrorCard errorText={TEXT_LIBRARY.SERVER_ERROR} />}
        {!globalSiteId && !loading && !hasSelectedChargerContext && (
          <div className="no-charger-selected">
            <div className="no-charger-selected__circle">
              <Search size={36} />
            </div>
            <h3 className="no-charger-selected__title">No Charger Selected</h3>
            <p className="no-charger-selected__description">
              Search and select a charger using the serial number to view details, metrics, and send commands.
            </p>
            <button onClick={onSerialEditorOpen} className="no-charger-selected__search-btn">
              <Search size={16} />
              Search for a Charger
            </button>
          </div>
        )}
        {!loading && !error && globalSiteId && config && (
          <>
            {activeTab === "overview" && renderOverviewTab(config)}
            {activeTab === "metrics" && (
              <div className="evs-metrics-content">
                <ChargerMetrics
                  embedded={true}
                  dateRange={dateRange}
                  headerActions={({ zoomExtremes, setZoomExtremes }) => (
                    <div className="evs-metrics-header">
                      <DateRangeToolbar
                        activePreset={activePreset}
                        onPresetChange={handlePresetChange}
                        onCustomChange={handleCustomChange}
                        showTimezone
                      />
                      <div className="evs-metrics-actions">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!zoomExtremes}
                          onClick={() => setZoomExtremes(null)}
                          sx={RESET_ZOOM_BUTTON_SX}
                        >
                          Reset Zoom
                        </Button>
                        <RefreshButton onClick={handleRefreshClick} />
                      </div>
                    </div>
                  )}
                />
              </div>
            )}
            {activeTab === "ocpp" && (
              <>
                <div className="evs-ocpp-content"></div>
                <OcppMessages
                  activePreset={activePreset}
                  onPresetChange={handlePresetChange}
                  onCustomChange={handleCustomChange}
                  onRefresh={handleRefreshClick}
                />
              </>
            )}
            {activeTab === "troubleshoot" && <Troubleshoot />}
            {activeTab === "events" && (
              <Events
                activePreset={activePreset}
                onPresetChange={handlePresetChange}
                onCustomChange={handleCustomChange}
                onRefresh={handleRefreshClick}
              />
            )}
            {activeTab === "commands" && checkFeatureAccess(FEATURES.COMMANDS) && (
              <>
                <SendCommands
                  serialNumber={globalSerialNumber}
                  siteId={globalSiteId}
                  sendCommand={sendCommand}
                  ratedCurrent={config?.ratedCurrent}
                />
                {commandHistory.length > 0 && (
                  <motion.div className="evs-cmd-history" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <div className="evs-cmd-history__title">Recent Command Activity</div>
                    {commandHistory.map(entry => (
                      <div key={entry.id} className={`evs-cmd-history__item evs-cmd-history__item--${entry.status}`}>
                        <span className="evs-cmd-history__action">
                          {entry.action === "restart" ? "Restart" : "Get Config"}
                        </span>
                        <span className={`evs-cmd-history__status evs-cmd-history__status--${entry.status}`}>
                          {entry.status === "pending"
                            ? "Sending..."
                            : entry.status === "success"
                              ? `✓ ${entry.result}`
                              : `✗ ${entry.result}`}
                        </span>
                        <span className="evs-cmd-history__time">{getRelativeTime(entry.time)}</span>
                      </div>
                    ))}
                  </motion.div>
                )}
              </>
            )}
          </>
        )}
      </div>
      {openChargerDetailsSlideout && (
        <SlideOutPanel open={openChargerDetailsSlideout} onClose={() => setOpenChargerDetailsSlideout(false)} />
      )}
    </div>
  );
}

export default EvSummary;
